import { useState, useEffect, useMemo } from "react";
import DynamicTable from "components/common/DynamicTable";
import TableHeader from "components/common/TableHeader";
import { dashboardString, stringConfig } from "config/site.string";
import { Breadcrumb, Divider, Form, Tabs, Tooltip } from "antd";
import {
  ActivityLogWrapper,
  ActivityWrapper,
  InfoWrapper,
} from "pages/protectedPages/ActivityLog/styles";
import { useNavigate } from "react-router-dom";
import Link from "antd/es/typography/Link";
import { TableHeading } from "components/common/TableHeader/styles";
import { SEVER_DATE_TIME_FORMAT } from "library/helpers/utils";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import moment from "moment";
import { isMobile } from "react-device-detect";
import {
  fetchDashboardActivityListRequest,
  fetchDashboardAvgDetailsRequest,
} from "redux/dashboard/actions";
import { useDispatch, useSelector } from "react-redux";
import { config } from "config/site.config";
import CustomLoader from "components/common/Loader/CustomLoader";
import { first, isEmpty } from "lodash";
import { loadState } from "library/helpers/sessionStorage";
import { useUserAssignedCustomers } from "components/hooks/useUserAssignedCustomers";
import { useUserAssignedVendors } from "components/hooks/useUserAssignedVendors";
import { InfoCircleOutlined } from "@ant-design/icons";
import { LinkText } from "./styles";
import DashboardFilters from "./FilterSection";

const DashboardReports = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = loadState("userInfo");
  const {
    customerCodes: customerCodesFromHook,
    customerDropdownOptions: masterCustomerDropdownList,
  } = useUserAssignedCustomers();
  const {
    vendorCodes: vendorCodesFromHook,
    vendorDropdownOptions: masterVendorDropdownList,
  } = useUserAssignedVendors();

  // Filter customers and vendors based on user permissions
  // Using hooks' filtered lists

  // Transform the filtered data structure to match the expected format
  const customerCodes = useMemo(
    () => customerCodesFromHook,
    [customerCodesFromHook]
  );
  const vendorCodes = useMemo(() => vendorCodesFromHook, [vendorCodesFromHook]);
  const [form] = Form.useForm();
  const [selectedViewBy, setSelectedViewBy] =
    useState<string>("CUSTOMERORVENDOR");
  const [selectedDateRange, setSelectedDateRange] =
    useState<string>("last7days");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [data, setData] = useState([]);
 const [activeTab, setActiveTab] = useState(() => {
  const hasOrderPermission = userDetails?.permissions.find(
    (permission: any) => permission.module === "Order Activity"
  );
  const hasInvoicePermission = userDetails?.permissions.find(
    (permission: any) => permission.module === "Invoice Activity"
  );
  
  // If user doesn't have order permission but has invoice, start with invoice tab
  if (!hasOrderPermission && hasInvoicePermission) {
    return "2";
  }
  // Otherwise default to order tab
  return "1";
});

  const { activityList, activityDateRangeList, activityAvgDetails, isLoading } =
    useSelector((state: any) => state.dashboard);
  const [activityAvgDetailsState, setactivityAvgDetailsState] =
    useState(activityAvgDetails);
  const [activityListState, setActivityListState] = useState(activityList);
  const [activityDateRangeListState, setActivityDateRangeListState] = useState(
    activityDateRangeList
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [billTo, setBillTo] = useState<string>("");

  useEffect(() => {
    setactivityAvgDetailsState(activityAvgDetails);
  }, [activityAvgDetails]);

  useEffect(() => {
    setActivityListState(activityList);
  }, [activityList]);

//   useEffect(() => {
//     const hasOrderPermission = userDetails?.permissions.find(
//       (permission: any) => permission.module === "Order Activity"
//     );
//     const hasInvoicePermission = userDetails?.permissions.find(
//       (permission: any) => permission.module === "Invoice Activity"
//     );
//     if (!hasOrderPermission && hasInvoicePermission) {
//       setActiveTab("2");
//     } else if (hasOrderPermission) {
//       setActiveTab("1");
//     }
//   }, [userDetails?.permissions]);

  useEffect(() => {
    setActivityDateRangeListState(activityDateRangeList);
  }, [activityDateRangeList]);

  const isCustomerView = selectedViewBy === "CUSTOMERORVENDOR";

  useEffect(() => {
    const codes = activeTab === "1" ? customerCodes : vendorCodes;
    setBillTo(codes.join(","));
  }, [customerCodes, vendorCodes, activeTab]);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    const isFetch =
      selectedDateRange?.toUpperCase() !== "CUSTOM" ||
      (startDate !== null && endDate !== null);
    if (isFetch && !isEmpty(billTo)) {
      const payload = {
        dateRangeType: selectedDateRange?.toUpperCase(),
        startDate: startDate?.format(SEVER_DATE_TIME_FORMAT),
        endDate: endDate?.format(SEVER_DATE_TIME_FORMAT),
        billTo: billTo,
      };
      dispatch(
        fetchDashboardAvgDetailsRequest(
          activeTab === "1"
            ? config.getDashboardAvgCounts
            : config.getDashboardInvoiceAvgCounts,
          payload
        )
      );

      const activityPayload = {
        ...payload,
        searchBy: selectedViewBy,
      };
      dispatch(
        fetchDashboardActivityListRequest(
          activeTab === "1"
            ? config.getDashboardActivityList
            : config.getDashboardInvoiceActivityList,
          activityPayload
        )
      );
    }
  }, [
    selectedDateRange,
    startDate,
    endDate,
    selectedViewBy,
    isInitialized,
    billTo,
  ]);

  useEffect(() => {
    if (selectedViewBy === "CUSTOMERORVENDOR") {
      setData(
        activityList?.map((item: any, index: number) => ({
          ...item,
          key: index + 1,
        }))
      );
    } else {
      setData(
        activityDateRangeList?.map((item: any, index: number) => ({
          ...item,
          key: index + 1,
        }))
      );
    }
  }, [activityListState, activityDateRangeListState, selectedViewBy]);

  const handleDateRangeChange = (e: any) => {
    const option = e.target.value;
    setSelectedDateRange(option);
    form.setFieldsValue({
      dateRange: option,
    });

    const date = new Date();
    let start: Dayjs = dayjs(date).subtract(1, "month");
    let end: Dayjs = dayjs(date);

    if (option !== "custom") {
      switch (option) {
        case "today":
          start = dayjs();
          break;
        case "last7days":
          start = dayjs().subtract(6, "day");
          break;
        case "thisMonth":
          start = dayjs().startOf("month");
          break;
        case "lastMonth":
          start = dayjs().subtract(1, "month").startOf("month");
          end = dayjs().subtract(1, "month").endOf("month");
          break;
        default:
          start = dayjs();
      }
    }
    setStartDate(start);
    setEndDate(end);

    form.setFieldsValue({
      startEndDate: [dayjs(start), dayjs(end)],
    });
  };

  const handleCustomDateChange = (dates: any) => {
    if (dates) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const onChange = (e: any) => {
    setSelectedViewBy(e.target.value);
  };

  const columnsData = [
    {
      title: isCustomerView
        ? activeTab === "1"
          ? dashboardString.customer
          : dashboardString.vendor
        : dashboardString.date,
      dataIndex: isCustomerView ? "company" : "emailReceivedOn",
      isSorter: true,
      width: "23%",
      render: (text: any) => {
        const customerLabel: any = first(
          masterCustomerDropdownList?.filter(
            (el: any) => el?.value?.toLowerCase() === text?.toLowerCase()
          )
        );
        const vendorLabel: any = first(
          masterVendorDropdownList?.filter(
            (el: any) => el?.value?.toLowerCase() === text?.toLowerCase()
          )
        );
        return isCustomerView
          ? activeTab === "1"
            ? customerLabel?.label
            : activeTab === "2"
            ? vendorLabel?.label
            : null
          : text
          ? moment(text).format("DD-MMM-YYYY")
          : null;
      },
    },
    {
      title: dashboardString.success,
      dataIndex: "success",
      render: (text: string, record: any) => {
        return (
          <Link
            onClick={() => {
              navigate(
                activeTab === "1" ? "/order-activity" : "/invoice-activity",
                {
                  state: {
                    activityStatus: "Success",
                    selectedDateRange: isCustomerView
                      ? selectedDateRange
                      : "custom",
                    customerName: isCustomerView ? record.company : billTo,
                    date: isCustomerView ? null : record.emailReceivedOn,
                    startDate: isCustomerView
                      ? startDate
                        ? startDate.toISOString()
                        : null
                      : record.emailReceivedOn,
                    endDate: isCustomerView
                      ? endDate
                        ? endDate.toISOString()
                        : null
                      : record.emailReceivedOn,
                  },
                }
              );
            }}
            style={{
              cursor: "pointer",
              color: "#1890ff",
              textDecoration: "underline",
            }}
          >
            {text}
          </Link>
        );
      },
    },
    {
      title: dashboardString.failed,
      dataIndex: "failed",
      width: "15%",
      render: (text: string, record: any) => {
        return (
          <LinkText
            onClick={() => {
              navigate(
                activeTab === "1" ? "/order-activity" : "/invoice-activity",
                {
                  state: {
                    activityStatus: "Failed",
                    selectedDateRange: isCustomerView
                      ? selectedDateRange
                      : "custom",
                    customerName: isCustomerView ? record.company : billTo,
                    date: isCustomerView ? null : record.emailReceivedOn,
                    startDate: isCustomerView
                      ? startDate
                        ? startDate.toISOString()
                        : null
                      : record.emailReceivedOn,
                    endDate: isCustomerView
                      ? endDate
                        ? endDate.toISOString()
                        : null
                      : record.emailReceivedOn,
                  },
                }
              );
            }}
            style={{
              cursor: "pointer",
              color: "#1890ff",
              textDecoration: "underline",
            }}
          >
            {text}
          </LinkText>
        );
      },
    },
    {
      title: dashboardString.resolved,
      dataIndex: "resolved",
      width: "15%",
      render: (text: string, record: any) => {
        return (
          <LinkText
            onClick={() => {
              navigate(
                activeTab === "1" ? "/order-activity" : "/invoice-activity",
                {
                  state: {
                    activityStatus: "Resolved",
                    selectedDateRange: isCustomerView
                      ? selectedDateRange
                      : "custom",
                    customerName: isCustomerView ? record.company : billTo,
                    date: isCustomerView ? null : record.emailReceivedOn,
                    startDate: isCustomerView
                      ? startDate
                        ? startDate.toISOString()
                        : null
                      : record.emailReceivedOn,
                    endDate: isCustomerView
                      ? endDate
                        ? endDate.toISOString()
                        : null
                      : record.emailReceivedOn,
                  },
                }
              );
            }}
            style={{
              cursor: "pointer",
              color: "#1890ff",
              textDecoration: "underline",
            }}
          >
            {text}
          </LinkText>
        );
      },
    },
    {
      title: dashboardString.processing,
      dataIndex: "processing",
      width: "15%",
      render: (text: string, record: any) => {
        return (
          <LinkText
            onClick={() => {
              navigate(
                activeTab === "1" ? "/order-activity" : "/invoice-activity",
                {
                  state: {
                    activityStatus: "Inqueue",
                    selectedDateRange: isCustomerView
                      ? selectedDateRange
                      : "custom",
                    customerName: isCustomerView ? record.company : billTo,
                    date: isCustomerView ? null : record.emailReceivedOn,
                    startDate: isCustomerView
                      ? startDate
                        ? startDate.toISOString()
                        : null
                      : record.emailReceivedOn,
                    endDate: isCustomerView
                      ? endDate
                        ? endDate.toISOString()
                        : null
                      : record.emailReceivedOn,
                  },
                }
              );
            }}
            style={{
              cursor: "pointer",
              color: "#1890ff",
              textDecoration: "underline",
            }}
          >
            {text}
          </LinkText>
        );
      },
    },
    {
      title: <>{dashboardString.total} </>,
      dataIndex: "total",
      width: "15%",
    },
  ];

  const items = [
    userDetails?.permissions.find(
      (permission: any) => permission.module === "Order Activity"
    ) && {
      key: "1",
      label: "Order Activity",
      children: <></>,
    },
    // userDetails?.permissions.find(
    //   (permission: any) => permission.module === "Invoice Activity"
    // ) && {
    //   key: "2",
    //   label: "Invoice Activity",
    //   children: <></>,
    // },
  ];

  return (
    <ActivityWrapper className="dashboard">
      <InfoWrapper>
        <Breadcrumb
          items={[
            {
              title: <TableHeading>Dashboard</TableHeading>,
            },
          ]}
        />
        <Tooltip
          title="Track customer statistics including success, failed, resolved, processing and total processed requests."
          placement="bottomRight"
          overlayInnerStyle={{ maxWidth: 300 }}
        >
          <InfoCircleOutlined style={{ cursor: "pointer" }} />
        </Tooltip>
      </InfoWrapper>
      <Divider style={{ marginBottom: 0, marginTop: 0 }} />
      {isLoading && <CustomLoader />}
      <div style={{ padding: "0px 10px" }}>
        <Tabs
          activeKey={activeTab}
          onChange={(e) => {
            setActiveTab(e);
          }}
          items={items}
        />
        <DashboardFilters
          selectedDateRange={selectedDateRange}
          onChange={onChange}
          handleCustomDateChange={handleCustomDateChange}
          handleDateRangeChange={handleDateRangeChange}
          form={form}
          label={
            activeTab === "1"
              ? dashboardString.customer
              : dashboardString.vendor
          }
          activityAvgDetailsState={activityAvgDetailsState}
        />
        <ActivityLogWrapper>
          <TableHeader
            placeHolder={stringConfig.searchPlaceholder}
            inputWidth={isMobile ? "100%" : 180}
            isNoSearchRequired={false}
            disableSearchBox={false}
            initialData={
              selectedViewBy === "Date"
                ? activityDateRangeListState
                : activityListState
            }
            isShowButton={false}
            leftHeaderText={
              selectedViewBy === "Date"
                ? selectedViewBy
                : activeTab === "1"
                ? dashboardString.customer
                : dashboardString.vendor
            }
            callBack={(filterData: any) => setData(filterData)}
            columnsKey={columnsData.map(({ dataIndex }) => dataIndex)}
          />
          <DynamicTable
            columns={columnsData}
            data={data}
            loading={false}
            initialData={
              selectedViewBy === "Date"
                ? activityDateRangeListState
                : activityListState
            }
            isServerSidePagination={false}
          />
        </ActivityLogWrapper>
      </div>
    </ActivityWrapper>
  );
};
export default DashboardReports;
